import React, { useState } from "react";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { RooflineMark } from "../shared/RooflineMark";
import { SectionHeader } from "../shared/SectionHeader";

// [Journey] [Trust] Oceanside's published 10-stage process. Source: https://www.oceansideliving.com.au/our-process
const processSteps = [{
  step: 1,
  title: "Initial consultation",
  headline: "Let's understand what you're looking to build.",
  description: "Every project starts with understanding your needs, your site and what you're hoping to achieve.",
  items: ["Explore our range of home designs", "Find a design that suits your block and lifestyle", "Discuss modifications and personalisation", "Understand your budget and priorities", "Explore custom design options", "Identify the best pathway for your project"]
}, {
  step: 2,
  title: "Pre-con agreement",
  headline: "Let's get the project moving.",
  description: "We outline the next stage of work, so you and Oceanside both understand what's involved before moving towards the building contract.",
  items: ["Confirming the project scope", "Confirming the proposed design direction", "Reviewing site-specific requirements", "Establishing the pre-construction services required", "Agreeing on the applicable pre-construction costs"]
}, {
  step: 3,
  title: "Design & costing",
  headline: "Creating the home and establishing the investment.",
  description: "We develop your design and balance design, functionality and budget so the home aligns with what you're looking to achieve.",
  items: ["Floor plan and functionality", "Site positioning and orientation", "External appearance", "Indoor and outdoor living", "Construction and project-specific costs", "Site survey and soil reports arranged"]
}, {
  step: 4,
  title: "Working drawings",
  headline: "Turning your design into a buildable project.",
  description: "This is where the design moves beyond a concept and becomes a detailed building project.",
  items: ["Detailed floor plans and elevations", "Sections and internal layouts", "Room dimensions", "Door and window details", "Electrical layouts", "Engineering requirements"]
}, {
  step: 5,
  title: "Contract signing",
  headline: "Making it official.",
  description: "We want you to feel confident about what you're signing and understand exactly what you're committing to.",
  items: ["Building contract", "Agreed scope of works", "Contract inclusions", "Contract price", "Relevant allowances", "Project responsibilities"]
}, {
  step: 6,
  title: "Selections",
  headline: "Make it yours.",
  description: "Our team helps you create a cohesive look while keeping your selections aligned with your overall vision.",
  items: ["Flooring and tiles", "Cabinetry and benchtops", "Tapware and sanitaryware", "Appliances and lighting", "Paint colours and hardware", "Optional upgrades"]
}, {
  step: 7,
  title: "Final plans",
  headline: "Every detail confirmed before we build.",
  description: "We make sure everything required to construct your home is clearly documented before moving forward.",
  items: ["Final floor plans and elevations", "Electrical plans", "Finishes and specifications", "Fixtures and fittings", "Selected upgrades", "Engineering and construction documentation"]
}, {
  step: 8,
  title: "Permits & approvals",
  headline: "Getting everything ready to build.",
  description: "Every site is different, so the approvals required depend on the nature and location of your project.",
  items: ["Building permit", "Planning and development approvals", "Energy efficiency requirements", "Site and soil requirements", "Bushfire or coastal requirements where applicable", "Stormwater and drainage"]
}, {
  step: 9,
  title: "Construction",
  headline: "From plans to reality.",
  description: "Each stage is quality checked and inspected. We keep you informed about progress, upcoming milestones and any decisions that need your attention.",
  items: ["Site start, earthworks and slab down", "Walls, roof frame and roof cover", "Windows, plumbing and electrical", "Linings, cabinetry and fit-out", "Tiling, painting and flooring", "External works and quality inspections"]
}, {
  step: 10,
  title: "Handover",
  headline: "Your home. Your keys. Your next chapter.",
  description: "We walk through your new home together and make sure you understand everything you need to know before you move in.",
  items: ["Final inspections", "Practical completion", "Defect identification and rectification", "Handover information", "Relevant warranties and manuals", "Keys and access"]
}];
type BuildingJourneyProps = {
  detailed?: boolean;
};
export function BuildingJourney({
  detailed = false
}: BuildingJourneyProps) {
  const [active, setActive] = useState(0);
  const current = processSteps[active];
  if (detailed) {
    return <ol className="space-y-4" data-id="element-108" data-mp="tf0sk7x7h1so">
        {processSteps.map(item => <li key={item.step} className="grid gap-6 rounded-[28px] border border-line bg-white p-7 md:p-10 lg:grid-cols-12" data-id="element-109" data-mp="pf0e4na3wqlm">
            <div className="lg:col-span-5" data-id="element-110" data-mp="134k9v959snlf">
              <span className="inline-flex items-center gap-3" data-id="element-111" data-mp="137pyyodwfph5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-charcoal font-heading text-[15px] font-semibold text-paper" data-id="element-112" data-mp="y7c304pko5zh">{item.step}</span>
                <span className="text-[15px] font-semibold text-slate" data-id="element-113" data-mp="6gec966rmvgn">{item.title}</span>
              </span>
              <h2 className="mt-4 text-[26px] font-semibold leading-tight tracking-tight" data-id="element-114" data-mp="kgf98ajwtiqu">{item.headline}</h2>
              <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-115" data-mp="ffjascibuost">{item.description}</p>
            </div>
            <ul className="grid content-start gap-3 sm:grid-cols-2 lg:col-span-7" data-id="element-116" data-mp="1vszp9wsbd7xa">
              {item.items.map(point => <li key={point} className="flex items-start gap-3 text-[16px]" data-id="element-117" data-mp="1qxu5b3ih23bu">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-118" data-mp="1yy8g5c28cpz3">
                    <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-119" data-mp="27x3mco59y02e" />
                  </span>
                  {point}
                </li>)}
            </ul>
          </li>)}
      </ol>;
  }
  return <section id="process" aria-labelledby="process-heading" className="bg-charcoal py-20 text-paper md:py-28" data-id="element-120" data-mp="1f97t5tewyqad">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-121" data-mp="1k817tp5xsnc4">
        <SectionHeader id="process-heading" tone="dark" title="Know exactly what happens next." intro="Ten clear stages from your first conversation to handover, so you always know what's happening, what's required and what's coming next." data-id="element-122" data-mp="1p6m6hk5jiyyg" />

        <div role="tablist" aria-label="Building stages" className="mt-12 grid grid-cols-2 gap-2 md:grid-cols-5" data-id="element-123" data-mp="11grvl84s7kgu">
          {processSteps.map((item, index) => {
          const selected = index === active;
          return <button key={item.step} role="tab" id={`stage-tab-${item.step}`} aria-selected={selected} aria-controls="stage-panel" tabIndex={selected ? 0 : -1} onClick={() => setActive(index)} onKeyDown={event => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const delta = event.key === "ArrowRight" ? 1 : -1;
            const nextIndex = (active + delta + processSteps.length) % processSteps.length;
            setActive(nextIndex);
            document.getElementById(`stage-tab-${processSteps[nextIndex].step}`)?.focus();
          }} className={`flex min-h-[56px] items-center gap-3 rounded-full border px-3 text-left transition-[background-color,border-color,color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean ${selected ? "border-ocean bg-ocean text-charcoal" : "border-paper/20 text-paper/80 hover:border-paper/50 hover:text-paper"}`} data-id="element-124" data-mp="oz7cd45zdb9k" data-mpi={index}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-heading text-[14px] font-semibold ${selected ? "bg-charcoal text-paper" : "bg-paper/10"}`} data-id="element-125" data-mp="164u86uuj1jhe">
                  {item.step}
                </span>
                <span className="text-[14px] font-semibold leading-tight md:text-[15px]" data-id="element-126" data-mp="ih2ubmtc8ye0">{item.title}</span>
              </button>;
        })}
        </div>

        <div id="stage-panel" role="tabpanel" aria-labelledby={`stage-tab-${current.step}`} className="mt-6 rounded-[28px] border border-paper/15 bg-[#332E2E] p-7 md:p-10" data-id="element-127" data-mp="ndtasb921vzy">
          <div className="grid gap-8 lg:grid-cols-12" data-id="element-128" data-mp="dggayhb9ir6w">
            <div className="lg:col-span-5" data-id="element-129" data-mp="3qyjzwbq6ed95">
              <span className="inline-flex items-center gap-3 text-[14px] font-semibold text-ocean" data-id="element-130" data-mp="3qzdbbq02xqxm">
                <RooflineMark data-id="element-131" data-mp="3m1biidcuqd8y" /> Stage {current.step} of {processSteps.length}
              </span>
              <h3 className="mt-3 text-[26px] font-semibold leading-tight tracking-tight md:text-[30px]" data-id="element-132" data-mp="3vp1ptntw92y">{current.headline}</h3>
              <p className="mt-3 text-[17px] leading-relaxed text-paper/75" data-id="element-133" data-mp="3v2rtam7kwhdp">{current.description}</p>
            </div>
            <ul className="grid content-start gap-3 sm:grid-cols-2 lg:col-span-7" data-id="element-134" data-mp="8tgroi4wxl4i">
              {current.items.map(point => <li key={point} className="flex items-start gap-3 rounded-2xl border border-paper/10 px-4 py-3 text-[15px] text-paper/90" data-id="element-135" data-mp="mjk9mwwreso5">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ocean" aria-hidden="true" data-id="element-136" data-mp="il4okyk8nudd" />
                  {point}
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-137" data-mp="rhvhx37eov49">
          <ButtonLink to="/our-process" variant="inverse" data-id="element-138" data-mp="354hwwggw527k">
            See our full building process <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-139" data-mp="30czxhwuin9op" />
          </ButtonLink>
        </div>
      </div>
    </section>;
}